import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth-context";
import { useClubInfo } from "../../lib/use-club-info";
import { HeaderBar } from "./header-bar";
import { BottomNav } from "./bottom-nav";
import { SideMenu, SideNav, isDesktop, useSideNavCollapsed } from "./side-menu";
import { useNavConfig } from "./nav-config";
import { SignalementSubjectProvider } from "../signalement/signalement-subject";
import { SignalementDialog } from "../signalement/flag-button";
import { DpaGate } from "../legal/dpa-gate";

/**
 * Chrome around every signed-in screen. Desktop: header plus a sidebar that is
 * part of the layout (full or icons only). Mobile: header, drawer and, for
 * coaches and students, a bottom bar.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const clubInfo = useClubInfo();
  const nav = useNavConfig();
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, toggleCollapsed] = useSideNavCollapsed();
  const [reportOpen, setReportOpen] = useState(false);
  // Impersonation is read-only: the API would refuse the report.
  const onReport = useMemo(() => (user?.impersonator ? undefined : () => setReportOpen(true)), [user?.impersonator]);

  const { home, items, settingsItems, switchItems, roleLabel, bottomItems } = nav;

  // The sidebar reads these on every route change; a fresh object or handler
  // would redraw every NavList row even when only the page body changed.
  const menuProps = useMemo(
    () => ({
      items,
      settingsItems,
      switchItems,
      activePath: location.pathname,
      onNavigate: navigate,
      userName: user?.name ?? "",
      roleLabel,
      onLogout: logout,
      onReport,
    }),
    [items, settingsItems, switchItems, location.pathname, navigate, user?.name, roleLabel, logout, onReport]
  );

  const onLogoClick = useCallback(() => navigate(home), [navigate, home]);
  const onMenuClick = useCallback(() => (isDesktop() ? toggleCollapsed() : setMenuOpen(true)), [toggleCollapsed]);
  const onMenuOpen = useCallback(() => setMenuOpen(true), []);
  const onMenuClose = useCallback(() => setMenuOpen(false), []);

  return (
    // The header's flag, the menu entry and the page below share what is being reported.
    <SignalementSubjectProvider>
      <HeaderBar clubInfo={clubInfo} onLogoClick={onLogoClick} onMenuClick={onMenuClick} hideMenuOnMobile={!!bottomItems} onReport={onReport} />
      <div className="md:flex" style={{ "--bottom-nav": bottomItems ? "4rem" : "0px" } as React.CSSProperties}>
        <SideNav {...menuProps} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
        <div className="min-w-0 flex-1">{children}</div>
      </div>
      <SideMenu {...menuProps} open={menuOpen} onClose={onMenuClose} />
      {bottomItems && (
        <BottomNav items={bottomItems} activePath={location.pathname} onNavigate={navigate} onMenuClick={onMenuOpen} />
      )}
      {reportOpen && <SignalementDialog onClose={() => setReportOpen(false)} />}
      <DpaGate />
    </SignalementSubjectProvider>
  );
}
